import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, Result, Skeleton, Typography, theme } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import PageTransition from '@/components/ui/PageTransition';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { useClientByIdQuery } from '@/api/auth/queries';
import { DEFAULT_CLIENTS_LIST_ROUTE_SEARCH } from '@/api/auth/defaults';
import { AdminLogsView, adminLogsSearchSchema, type AdminLogsSearch } from '@/components/logs';

const { Text } = Typography;

export const Route = createFileRoute('/admin/tenants/$tenantId/logs')({
  validateSearch: (search) => adminLogsSearchSchema.parse(search),
  component: TenantLogsPage
});

/**
 * Logs of one tenant: its flow executions and (for admins) the audit trail of its flows,
 * at tenant level or per company.
 */
function TenantLogsPage() {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { tenantId } = Route.useParams();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { data, isLoading, error } = useClientByIdQuery(tenantId);
  const client = data?.client;

  const handleSearchChange = (patch: Partial<AdminLogsSearch>) =>
    navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true });

  const backTo = `/admin/tenants/${tenantId}`;

  if (isLoading) {
    return (
      <PageTransition animation="fadeInUp">
        <Skeleton active paragraph={{ rows: 6 }} />
      </PageTransition>
    );
  }

  if (error || !client) {
    return (
      <PageTransition animation="fadeInUp">
        <DetailPageHeader
          icon={<FileTextOutlined />}
          title={t('tenants.logs.title')}
          backTo="/admin/tenants"
          backSearch={DEFAULT_CLIENTS_LIST_ROUTE_SEARCH}
          backLabel={t('tenants.detail.backToClients')}
        />
        <Card>
          <Result status="warning" title={t('tenants.logs.title')} subTitle={t('tenants.logs.clientNotFound')} />
        </Card>
      </PageTransition>
    );
  }

  return (
    <PageTransition animation="fadeInUp">
      <DetailPageHeader
        icon={<FileTextOutlined />}
        title={t('tenants.logs.clientLogsFor', { name: client.name })}
        meta={<Text type="secondary">{t('tenants.logs.subtitle')}</Text>}
        backTo={backTo}
        backLabel={t('tenants.detail.backToTenant')}
      />

      <AdminLogsView
        search={search}
        onSearchChange={handleSearchChange}
        fixedClientId={tenantId}
        tenantCompanies={client.companies}
        exportName={client.key}
      />
    </PageTransition>
  );
}
